<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Typography Component
      p.text--secondary Typography system for consistent text styling across the application.

    // Heading Typography
    .component-section.mb-8#heading-typography
      h3.mb-4.cursor-pointer(@click="navigateToSection('heading-typography')") Heading Typography
      .variations-grid
        .variation-item.mb-6(v-for="heading in headingVariations" :key="heading.name" :id="heading.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(heading.id)") {{ heading.name }}
            p.text--secondary.caption {{ heading.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .d-flex.gap-6.flex-column
              div
                div(:class="heading.class") {{ heading.text }}
              .css-properties
                .text-caption.font-weight-medium.mb-2 CSS Properties:
                .css-props.text-caption.font-family-monospace(v-for="prop in heading.cssProps" :key="prop.name")
                  span.property-name {{ prop.name }}:
                  span.property-value.ml-1 {{ prop.value }};

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === heading.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === heading.code ? 'success' : ''" @click="copyCode(heading.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ heading.code }}

    // Body Typography
    .component-section.mb-8#body-typography
      h3.mb-4.cursor-pointer(@click="navigateToSection('body-typography')") Body Typography
      .variations-grid
        .variation-item.mb-6(v-for="bodyType in bodyVariations" :key="bodyType.name" :id="bodyType.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(bodyType.id)") {{ bodyType.name }}
            p.text--secondary.caption {{ bodyType.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .d-flex.gap-6.flex-column
              div
                div(:class="bodyType.class") {{ bodyType.text }}
              .css-properties
                .text-caption.font-weight-medium.mb-2 CSS Properties:
                .css-props.text-caption.font-family-monospace(v-for="prop in bodyType.cssProps" :key="prop.name")
                  span.property-name {{ prop.name }}:
                  span.property-value.ml-1 {{ prop.value }};

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === bodyType.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === bodyType.code ? 'success' : ''" @click="copyCode(bodyType.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ bodyType.code }}

    // Link Typography
    .component-section.mb-8#link-typography
      h3.mb-4.cursor-pointer(@click="navigateToSection('link-typography')") Link Typography
      .variations-grid
        .variation-item.mb-6(v-for="link in linkVariations" :key="link.name" :id="link.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(link.id)") {{ link.name }}
            p.text--secondary.caption {{ link.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .d-flex.gap-6.flex-column
              div
                div(:class="link.class") {{ link.text }}
              .css-properties
                .text-caption.font-weight-medium.mb-2 CSS Properties:
                .css-props.text-caption.font-family-monospace(v-for="prop in link.cssProps" :key="prop.name")
                  span.property-name {{ prop.name }}:
                  span.property-value.ml-1 {{ prop.value }};

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === link.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === link.code ? 'success' : ''" @click="copyCode(link.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ link.code }}

    // Rounded Typography
    .component-section.mb-8#rounded-typography
      h3.mb-4.cursor-pointer(@click="navigateToSection('rounded-typography')") Rounded Typography
      p.text--secondary.mb-4 Rounded typography uses Gotham Rounded font family for numerical displays and special use cases where a softer, more approachable appearance is needed.
      .variations-grid
        .variation-item.mb-6(v-for="rounded in roundedVariations" :key="rounded.name" :id="rounded.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(rounded.id)") {{ rounded.name }}
            p.text--secondary.caption {{ rounded.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .d-flex.gap-6.flex-column
              div
                div(:class="rounded.class") {{ rounded.text }}
              .css-properties
                .text-caption.font-weight-medium.mb-2 CSS Properties:
                .css-props.text-caption.font-family-monospace(v-for="prop in rounded.cssProps" :key="prop.name")
                  span.property-name {{ prop.name }}:
                  span.property-value.ml-1 {{ prop.value }};

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === rounded.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === rounded.code ? 'success' : ''" @click="copyCode(rounded.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ rounded.code }}

    // CSS Variables Documentation
    .component-section.mb-8#css-variables
      h3.mb-4.cursor-pointer(@click="navigateToSection('css-variables')") CSS Variables
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Variable
                th Default Value
                th Description
            tbody
              tr(v-for="variable in cssVariables" :key="variable.name")
                td.font-weight-medium.text-no-wrap {{ variable.name }}
                td.text--secondary.text-no-wrap {{ variable.value }}
                td.single-line {{ variable.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      headingVariations: [
        {
          name: 'Heading 1',
          id: 'heading-1',
          description: 'Largest heading for main page titles',
          class: 'yz-heading-1',
          text: 'The quick brown fox jumps over the lazy dog',
          code: '.yz-heading-1 Your text here',
          cssProps: [
            { name: 'font-family', value: 'Inter, sans-serif' },
            { name: 'font-size', value: '56px' },
            { name: 'font-weight', value: '600' },
            { name: 'line-height', value: '64px' },
            { name: 'letter-spacing', value: '-0.022em' }
          ]
        },
        {
          name: 'Heading 2',
          id: 'heading-2',
          description: 'Secondary heading for major sections',
          class: 'yz-heading-2',
          text: 'The quick brown fox jumps over the lazy dog',
          code: '.yz-heading-2 Your text here',
          cssProps: [
            { name: 'font-family', value: 'Inter, sans-serif' },
            { name: 'font-size', value: '48px' },
            { name: 'font-weight', value: '600' },
            { name: 'line-height', value: '56px' },
            { name: 'letter-spacing', value: '-0.022em' }
          ]
        },
        {
          name: 'Heading 3',
          id: 'heading-3',
          description: 'Tertiary heading for subsections',
          class: 'yz-heading-3',
          text: 'The quick brown fox jumps over the lazy dog',
          code: '.yz-heading-3 Your text here',
          cssProps: [
            { name: 'font-family', value: 'Inter, sans-serif' },
            { name: 'font-size', value: '40px' },
            { name: 'font-weight', value: '600' },
            { name: 'line-height', value: '48px' },
            { name: 'letter-spacing', value: '-0.022em' }
          ]
        },
        {
          name: 'Heading 4',
          id: 'heading-4',
          description: 'Medium heading for content sections',
          class: 'yz-heading-4',
          text: 'The quick brown fox jumps over the lazy dog',
          code: '.yz-heading-4 Your text here',
          cssProps: [
            { name: 'font-family', value: 'Inter, sans-serif' },
            { name: 'font-size', value: '32px' },
            { name: 'font-weight', value: '600' },
            { name: 'line-height', value: '40px' },
            { name: 'letter-spacing', value: '-0.021em' }
          ]
        },
        {
          name: 'Heading 5',
          id: 'heading-5',
          description: 'Small heading for component titles',
          class: 'yz-heading-5',
          text: 'The quick brown fox jumps over the lazy dog',
          code: '.yz-heading-5 Your text here',
          cssProps: [
            { name: 'font-family', value: 'Inter, sans-serif' },
            { name: 'font-size', value: '24px' },
            { name: 'font-weight', value: '600' },
            { name: 'line-height', value: '32px' },
            { name: 'letter-spacing', value: '-0.019em' }
          ]
        },
        {
          name: 'Heading 6',
          id: 'heading-6',
          description: 'Smallest heading for minor sections',
          class: 'yz-heading-6',
          text: 'The quick brown fox jumps over the lazy dog',
          code: '.yz-heading-6 Your text here',
          cssProps: [
            { name: 'font-family', value: 'Inter, sans-serif' },
            { name: 'font-size', value: '20px' },
            { name: 'font-weight', value: '600' },
            { name: 'line-height', value: '32px' },
            { name: 'letter-spacing', value: '-0.017em' }
          ]
        },
        {
          name: 'Heading 7',
          id: 'heading-7',
          description: 'Micro heading for labels and captions',
          class: 'yz-heading-7',
          text: 'The quick brown fox jumps over the lazy dog',
          code: '.yz-heading-7 Your text here',
          cssProps: [
            { name: 'font-family', value: 'Inter, sans-serif' },
            { name: 'font-size', value: '18px' },
            { name: 'font-weight', value: '600' },
            { name: 'line-height', value: '28px' },
            { name: 'letter-spacing', value: '-0.014em' }
          ]
        },
        {
          name: 'Heading 8',
          id: 'heading-8',
          description: 'Minimal heading for inline titles',
          class: 'yz-heading-8',
          text: 'The quick brown fox jumps over the lazy dog',
          code: '.yz-heading-8 Your text here',
          cssProps: [
            { name: 'font-family', value: 'Inter, sans-serif' },
            { name: 'font-size', value: '16px' },
            { name: 'font-weight', value: '600' },
            { name: 'line-height', value: '24px' },
            { name: 'letter-spacing', value: '-0.011em' }
          ]
        },
        {
          name: 'Heading 9',
          id: 'heading-9',
          description: 'Extra small heading for fine details',
          class: 'yz-heading-9',
          text: 'The quick brown fox jumps over the lazy dog',
          code: '.yz-heading-9 Your text here',
          cssProps: [
            { name: 'font-family', value: 'Inter, sans-serif' },
            { name: 'font-size', value: '14px' },
            { name: 'font-weight', value: '600' },
            { name: 'line-height', value: '20px' },
            { name: 'letter-spacing', value: '-0.011em' }
          ]
        },
        {
          name: 'Heading 10',
          id: 'heading-10',
          description: 'Tiny heading for micro content',
          class: 'yz-heading-10',
          text: 'The quick brown fox jumps over the lazy dog',
          code: '.yz-heading-10 Your text here',
          cssProps: [
            { name: 'font-family', value: 'Inter, sans-serif' },
            { name: 'font-size', value: '12px' },
            { name: 'font-weight', value: '600' },
            { name: 'line-height', value: '16px' },
            { name: 'letter-spacing', value: '-0.011em' }
          ]
        }
      ],
      bodyVariations: [
        {
          name: 'Regular Body XL',
          id: 'regular-body-xl',
          description: 'Extra large body text for prominent content',
          class: 'yz-regular-body-xl',
          text: 'The quick brown fox jumps over the lazy dog. This is an example of regular body text in extra large size.',
          code: '.yz-regular-body-xl Your text here',
          cssProps: [
            { name: 'font-family', value: 'Inter, sans-serif' },
            { name: 'font-size', value: '20px' },
            { name: 'font-weight', value: '400' },
            { name: 'line-height', value: '32px' },
            { name: 'letter-spacing', value: '-0.017em' }
          ]
        },
        {
          name: 'Regular Body Large',
          id: 'regular-body-lg',
          description: 'Large body text for important content',
          class: 'yz-regular-body-lg',
          text: 'The quick brown fox jumps over the lazy dog. This is an example of regular body text in large size.',
          code: '.yz-regular-body-lg Your text here',
          cssProps: [
            { name: 'font-family', value: 'Inter, sans-serif' },
            { name: 'font-size', value: '18px' },
            { name: 'font-weight', value: '400' },
            { name: 'line-height', value: '28px' },
            { name: 'letter-spacing', value: '-0.014em' }
          ]
        },
        {
          name: 'Regular Body Medium',
          id: 'regular-body-md',
          description: 'Standard body text for main content',
          class: 'yz-regular-body-md',
          text: 'The quick brown fox jumps over the lazy dog. This is an example of regular body text in medium size.',
          code: '.yz-regular-body-md Your text here',
          cssProps: [
            { name: 'font-family', value: 'Inter, sans-serif' },
            { name: 'font-size', value: '16px' },
            { name: 'font-weight', value: '400' },
            { name: 'line-height', value: '24px' },
            { name: 'letter-spacing', value: '-0.011em' }
          ]
        },
        {
          name: 'Regular Body Small',
          id: 'regular-body-sm',
          description: 'Small body text for supporting content',
          class: 'yz-regular-body-sm',
          text: 'The quick brown fox jumps over the lazy dog. This is an example of regular body text in small size.',
          code: '.yz-regular-body-sm Your text here',
          cssProps: [
            { name: 'font-size', value: '14px' },
            { name: 'font-weight', value: '400' },
            { name: 'line-height', value: '20px' },
            { name: 'letter-spacing', value: '-0.006em' }
          ]
        },
        {
          name: 'Regular Body XS',
          id: 'regular-body-xs',
          description: 'Extra small body text for captions',
          class: 'yz-regular-body-xs',
          text: 'The quick brown fox jumps over the lazy dog. This is an example of regular body text in extra small size.',
          code: '.yz-regular-body-xs Your text here',
          cssProps: [
            { name: 'font-size', value: '12px' },
            { name: 'font-weight', value: '400' },
            { name: 'line-height', value: '16px' }
          ]
        },
        {
          name: 'Regular Body 2XS',
          id: 'regular-body-2xs',
          description: 'Tiny body text for fine print',
          class: 'yz-regular-body-2xs',
          text: 'The quick brown fox jumps over the lazy dog. This is an example of regular body text in tiny size.',
          code: '.yz-regular-body-2xs Your text here',
          cssProps: [
            { name: 'font-size', value: '10px' },
            { name: 'font-weight', value: '400' },
            { name: 'line-height', value: '16px' }
          ]
        },
        {
          name: 'Medium Body XL',
          id: 'medium-body-xl',
          description: 'Medium weight extra large text',
          class: 'yz-medium-body-xl',
          text: 'The quick brown fox jumps over the lazy dog. This is an example of medium body text in extra large size.',
          code: '.yz-medium-body-xl Your text here',
          cssProps: [
            { name: 'font-size', value: '20px' },
            { name: 'font-weight', value: '500' },
            { name: 'line-height', value: '32px' },
            { name: 'letter-spacing', value: '-0.017em' }
          ]
        },
        {
          name: 'Medium Body Large',
          id: 'medium-body-lg',
          description: 'Medium weight large text',
          class: 'yz-medium-body-lg',
          text: 'The quick brown fox jumps over the lazy dog. This is an example of medium body text in large size.',
          code: '.yz-medium-body-lg Your text here',
          cssProps: [
            { name: 'font-size', value: '18px' },
            { name: 'font-weight', value: '500' },
            { name: 'line-height', value: '28px' },
            { name: 'letter-spacing', value: '-0.014em' }
          ]
        },
        {
          name: 'Medium Body Medium',
          id: 'medium-body-md',
          description: 'Medium weight standard text',
          class: 'yz-medium-body-md',
          text: 'The quick brown fox jumps over the lazy dog. This is an example of medium body text in medium size.',
          code: '.yz-medium-body-md Your text here',
          cssProps: [
            { name: 'font-size', value: '16px' },
            { name: 'font-weight', value: '500' },
            { name: 'line-height', value: '24px' },
            { name: 'letter-spacing', value: '-0.011em' }
          ]
        },
        {
          name: 'Medium Body Small',
          id: 'medium-body-sm',
          description: 'Medium weight small text',
          class: 'yz-medium-body-sm',
          text: 'The quick brown fox jumps over the lazy dog. This is an example of medium body text in small size.',
          code: '.yz-medium-body-sm Your text here',
          cssProps: [
            { name: 'font-size', value: '14px' },
            { name: 'font-weight', value: '500' },
            { name: 'line-height', value: '20px' },
            { name: 'letter-spacing', value: '-0.006em' }
          ]
        },
        {
          name: 'Bold Body XL',
          id: 'bold-body-xl',
          description: 'Bold weight extra large text',
          class: 'yz-bold-body-xl',
          text: 'The quick brown fox jumps over the lazy dog. This is an example of bold body text in extra large size.',
          code: '.yz-bold-body-xl Your text here',
          cssProps: [
            { name: 'font-size', value: '20px' },
            { name: 'font-weight', value: '700' },
            { name: 'line-height', value: '32px' },
            { name: 'letter-spacing', value: '-0.017em' }
          ]
        },
        {
          name: 'Bold Body Large',
          id: 'bold-body-lg',
          description: 'Bold weight large text',
          class: 'yz-bold-body-lg',
          text: 'The quick brown fox jumps over the lazy dog. This is an example of bold body text in large size.',
          code: '.yz-bold-body-lg Your text here',
          cssProps: [
            { name: 'font-size', value: '18px' },
            { name: 'font-weight', value: '700' },
            { name: 'line-height', value: '28px' },
            { name: 'letter-spacing', value: '-0.014em' }
          ]
        },
        {
          name: 'Bold Body Medium',
          id: 'bold-body-md',
          description: 'Bold weight standard text',
          class: 'yz-bold-body-md',
          text: 'The quick brown fox jumps over the lazy dog. This is an example of bold body text in medium size.',
          code: '.yz-bold-body-md Your text here',
          cssProps: [
            { name: 'font-size', value: '16px' },
            { name: 'font-weight', value: '700' },
            { name: 'line-height', value: '24px' },
            { name: 'letter-spacing', value: '-0.011em' }
          ]
        },
        {
          name: 'Bold Body Small',
          id: 'bold-body-sm',
          description: 'Bold weight small text',
          class: 'yz-bold-body-sm',
          text: 'The quick brown fox jumps over the lazy dog. This is an example of bold body text in small size.',
          code: '.yz-bold-body-sm Your text here',
          cssProps: [
            { name: 'font-family', value: 'Inter, sans-serif' },
            { name: 'font-size', value: '14px' },
            { name: 'font-weight', value: '700' },
            { name: 'line-height', value: '20px' },
            { name: 'letter-spacing', value: '-0.006em' }
          ]
        }
      ],
      linkVariations: [
        {
          name: 'Link Body Large',
          id: 'link-body-lg',
          description: 'Large underlined link text',
          class: 'yz-link-body-lg',
          text: 'This is a link example',
          code: '.yz-link-body-lg Your link here',
          cssProps: [
            { name: 'font-family', value: 'Inter, sans-serif' },
            { name: 'font-size', value: '18px' },
            { name: 'font-weight', value: '400' },
            { name: 'line-height', value: '28px' },
            { name: 'letter-spacing', value: '-0.014em' },
            { name: 'text-decoration', value: 'underline' }
          ]
        },
        {
          name: 'Link Body Medium',
          id: 'link-body-md',
          description: 'Medium underlined link text',
          class: 'yz-link-body-md',
          text: 'This is a link example',
          code: '.yz-link-body-md Your link here',
          cssProps: [
            { name: 'font-family', value: 'Inter, sans-serif' },
            { name: 'font-size', value: '16px' },
            { name: 'font-weight', value: '400' },
            { name: 'line-height', value: '24px' },
            { name: 'letter-spacing', value: '-0.011em' },
            { name: 'text-decoration', value: 'underline' }
          ]
        },
        {
          name: 'Link Body Small',
          id: 'link-body-sm',
          description: 'Small underlined link text',
          class: 'yz-link-body-sm',
          text: 'This is a link example',
          code: '.yz-link-body-sm Your link here',
          cssProps: [
            { name: 'font-family', value: 'Inter, sans-serif' },
            { name: 'font-size', value: '14px' },
            { name: 'font-weight', value: '400' },
            { name: 'line-height', value: '20px' },
            { name: 'letter-spacing', value: '-0.006em' },
            { name: 'text-decoration', value: 'underline' }
          ]
        },
        {
          name: 'Link Button Large',
          id: 'link-button-lg',
          description: 'Large button-style link text',
          class: 'yz-link-button-lg',
          text: 'Button link example',
          code: '.yz-link-button-lg Your button link here',
          cssProps: [
            { name: 'font-family', value: 'Inter, sans-serif' },
            { name: 'font-size', value: '18px' },
            { name: 'font-weight', value: '600' },
            { name: 'line-height', value: '28px' },
            { name: 'letter-spacing', value: '-0.014em' },
            { name: 'text-decoration', value: 'none' },
            { name: 'cursor', value: 'pointer' }
          ]
        },
        {
          name: 'Link Button Medium',
          id: 'link-button-md',
          description: 'Medium button-style link text',
          class: 'yz-link-button-md',
          text: 'Button link example',
          code: '.yz-link-button-md Your button link here',
          cssProps: [
            { name: 'font-family', value: 'Inter, sans-serif' },
            { name: 'font-size', value: '16px' },
            { name: 'font-weight', value: '600' },
            { name: 'line-height', value: '24px' },
            { name: 'letter-spacing', value: '-0.011em' },
            { name: 'text-decoration', value: 'none' },
            { name: 'cursor', value: 'pointer' }
          ]
        },
        {
          name: 'Link Button Small',
          id: 'link-button-sm',
          description: 'Small button-style link text',
          class: 'yz-link-button-sm',
          text: 'Button link example',
          code: '.yz-link-button-sm Your button link here',
          cssProps: [
            { name: 'font-family', value: 'Inter, sans-serif' },
            { name: 'font-size', value: '14px' },
            { name: 'font-weight', value: '600' },
            { name: 'line-height', value: '20px' },
            { name: 'letter-spacing', value: '-0.006em' },
            { name: 'text-decoration', value: 'none' },
            { name: 'cursor', value: 'pointer' }
          ]
        }
      ],
      roundedVariations: [
        {
          name: 'Rounded Medium 7',
          id: 'rounded-medium-7',
          description: 'Medium rounded typography for numerical displays and labels',
          class: 'yz-rounded-medium-7',
          text: '1,234.56',
          code: '.yz-rounded-medium-7 Your text here',
          cssProps: [
            { name: 'font-family', value: "'Gotham Rounded', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif" },
            { name: 'font-size', value: '18px' },
            { name: 'font-weight', value: '350' },
            { name: 'line-height', value: '28px' },
            { name: 'letter-spacing', value: '-0.014em' }
          ]
        }
      ],
      cssVariables: [
        // Font Families
        {
          name: '--yz-font-family',
          value: "'Inter', sans-serif",
          description: 'Primary font family for all text'
        },
        {
          name: '--yz-font-gotham-rounded',
          value: "'Gotham Rounded', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif",
          description: 'Rounded font family for special typography'
        },

        // Heading Typography Variables (yz-heading-1 through yz-heading-10)
        {
          name: '--yz-font-heading-1-size',
          value: '56px',
          description: 'Font size for heading 1'
        },
        {
          name: '--yz-font-heading-1-weight',
          value: '600',
          description: 'Font weight for heading 1'
        },
        {
          name: '--yz-font-heading-1-line-height',
          value: '64px',
          description: 'Line height for heading 1'
        },
        {
          name: '--yz-font-heading-1-letter-spacing',
          value: '-0.022em',
          description: 'Letter spacing for heading 1'
        },
        {
          name: '--yz-font-heading-2-size',
          value: '48px',
          description: 'Font size for heading 2'
        },
        {
          name: '--yz-font-heading-2-weight',
          value: '600',
          description: 'Font weight for heading 2'
        },
        {
          name: '--yz-font-heading-2-line-height',
          value: '56px',
          description: 'Line height for heading 2'
        },
        {
          name: '--yz-font-heading-2-letter-spacing',
          value: '-0.022em',
          description: 'Letter spacing for heading 2'
        },
        {
          name: '--yz-font-heading-3-size',
          value: '40px',
          description: 'Font size for heading 3'
        },
        {
          name: '--yz-font-heading-3-weight',
          value: '600',
          description: 'Font weight for heading 3'
        },
        {
          name: '--yz-font-heading-3-line-height',
          value: '48px',
          description: 'Line height for heading 3'
        },
        {
          name: '--yz-font-heading-3-letter-spacing',
          value: '-0.022em',
          description: 'Letter spacing for heading 3'
        },
        {
          name: '--yz-font-heading-4-size',
          value: '32px',
          description: 'Font size for heading 4'
        },
        {
          name: '--yz-font-heading-4-weight',
          value: '600',
          description: 'Font weight for heading 4'
        },
        {
          name: '--yz-font-heading-4-line-height',
          value: '40px',
          description: 'Line height for heading 4'
        },
        {
          name: '--yz-font-heading-4-letter-spacing',
          value: '-0.021em',
          description: 'Letter spacing for heading 4'
        },
        {
          name: '--yz-font-heading-5-size',
          value: '24px',
          description: 'Font size for heading 5'
        },
        {
          name: '--yz-font-heading-5-weight',
          value: '600',
          description: 'Font weight for heading 5'
        },
        {
          name: '--yz-font-heading-5-line-height',
          value: '32px',
          description: 'Line height for heading 5'
        },
        {
          name: '--yz-font-heading-5-letter-spacing',
          value: '-0.019em',
          description: 'Letter spacing for heading 5'
        },
        {
          name: '--yz-font-heading-6-size',
          value: '20px',
          description: 'Font size for heading 6'
        },
        {
          name: '--yz-font-heading-6-weight',
          value: '600',
          description: 'Font weight for heading 6'
        },
        {
          name: '--yz-font-heading-6-line-height',
          value: '32px',
          description: 'Line height for heading 6'
        },
        {
          name: '--yz-font-heading-6-letter-spacing',
          value: '-0.017em',
          description: 'Letter spacing for heading 6'
        },
        {
          name: '--yz-font-heading-7-size',
          value: '18px',
          description: 'Font size for heading 7'
        },
        {
          name: '--yz-font-heading-7-weight',
          value: '600',
          description: 'Font weight for heading 7'
        },
        {
          name: '--yz-font-heading-7-line-height',
          value: '28px',
          description: 'Line height for heading 7'
        },
        {
          name: '--yz-font-heading-7-letter-spacing',
          value: '-0.014em',
          description: 'Letter spacing for heading 7'
        },
        {
          name: '--yz-font-heading-8-size',
          value: '16px',
          description: 'Font size for heading 8'
        },
        {
          name: '--yz-font-heading-8-weight',
          value: '600',
          description: 'Font weight for heading 8'
        },
        {
          name: '--yz-font-heading-8-line-height',
          value: '24px',
          description: 'Line height for heading 8'
        },
        {
          name: '--yz-font-heading-8-letter-spacing',
          value: '-0.011em',
          description: 'Letter spacing for heading 8'
        },
        {
          name: '--yz-font-heading-9-size',
          value: '14px',
          description: 'Font size for heading 9'
        },
        {
          name: '--yz-font-heading-9-weight',
          value: '600',
          description: 'Font weight for heading 9'
        },
        {
          name: '--yz-font-heading-9-line-height',
          value: '20px',
          description: 'Line height for heading 9'
        },
        {
          name: '--yz-font-heading-9-letter-spacing',
          value: '-0.011em',
          description: 'Letter spacing for heading 9'
        },
        {
          name: '--yz-font-heading-10-size',
          value: '12px',
          description: 'Font size for heading 10'
        },
        {
          name: '--yz-font-heading-10-weight',
          value: '600',
          description: 'Font weight for heading 10'
        },
        {
          name: '--yz-font-heading-10-line-height',
          value: '16px',
          description: 'Line height for heading 10'
        },
        {
          name: '--yz-font-heading-10-letter-spacing',
          value: '-0.011em',
          description: 'Letter spacing for heading 10'
        },

        // Regular Body Typography Variables
        {
          name: '--yz-font-regular-body-xl-size',
          value: '20px',
          description: 'Font size for regular body XL text'
        },
        {
          name: '--yz-font-regular-body-xl-weight',
          value: '400',
          description: 'Font weight for regular body XL text'
        },
        {
          name: '--yz-font-regular-body-xl-line-height',
          value: '32px',
          description: 'Line height for regular body XL text'
        },
        {
          name: '--yz-font-regular-body-xl-letter-spacing',
          value: '-0.017em',
          description: 'Letter spacing for regular body XL text'
        },
        {
          name: '--yz-font-regular-body-lg-size',
          value: '18px',
          description: 'Font size for regular body large text'
        },
        {
          name: '--yz-font-regular-body-lg-weight',
          value: '400',
          description: 'Font weight for regular body large text'
        },
        {
          name: '--yz-font-regular-body-lg-line-height',
          value: '28px',
          description: 'Line height for regular body large text'
        },
        {
          name: '--yz-font-regular-body-lg-letter-spacing',
          value: '-0.014em',
          description: 'Letter spacing for regular body large text'
        },
        {
          name: '--yz-font-regular-body-md-size',
          value: '16px',
          description: 'Font size for regular body medium text'
        },
        {
          name: '--yz-font-regular-body-md-weight',
          value: '400',
          description: 'Font weight for regular body medium text'
        },
        {
          name: '--yz-font-regular-body-md-line-height',
          value: '24px',
          description: 'Line height for regular body medium text'
        },
        {
          name: '--yz-font-regular-body-md-letter-spacing',
          value: '-0.011em',
          description: 'Letter spacing for regular body medium text'
        },
        {
          name: '--yz-font-regular-body-sm-size',
          value: '14px',
          description: 'Font size for regular body small text'
        },
        {
          name: '--yz-font-regular-body-sm-weight',
          value: '400',
          description: 'Font weight for regular body small text'
        },
        {
          name: '--yz-font-regular-body-sm-line-height',
          value: '20px',
          description: 'Line height for regular body small text'
        },
        {
          name: '--yz-font-regular-body-sm-letter-spacing',
          value: '-0.006em',
          description: 'Letter spacing for regular body small text'
        },
        {
          name: '--yz-font-regular-body-xs-size',
          value: '12px',
          description: 'Font size for regular body XS text'
        },
        {
          name: '--yz-font-regular-body-xs-weight',
          value: '400',
          description: 'Font weight for regular body XS text'
        },
        {
          name: '--yz-font-regular-body-xs-line-height',
          value: '16px',
          description: 'Line height for regular body XS text'
        },
        {
          name: '--yz-font-regular-body-xs-letter-spacing',
          value: '0em',
          description: 'Letter spacing for regular body XS text'
        },
        {
          name: '--yz-font-regular-body-2xs-size',
          value: '10px',
          description: 'Font size for regular body 2XS text'
        },
        {
          name: '--yz-font-regular-body-2xs-weight',
          value: '400',
          description: 'Font weight for regular body 2XS text'
        },
        {
          name: '--yz-font-regular-body-2xs-line-height',
          value: '16px',
          description: 'Line height for regular body 2XS text'
        },
        {
          name: '--yz-font-regular-body-2xs-letter-spacing',
          value: '0em',
          description: 'Letter spacing for regular body 2XS text'
        },

        // Medium Body Typography Variables
        {
          name: '--yz-font-medium-body-xl-size',
          value: '20px',
          description: 'Font size for medium body XL text'
        },
        {
          name: '--yz-font-medium-body-xl-weight',
          value: '500',
          description: 'Font weight for medium body XL text'
        },
        {
          name: '--yz-font-medium-body-xl-line-height',
          value: '32px',
          description: 'Line height for medium body XL text'
        },
        {
          name: '--yz-font-medium-body-xl-letter-spacing',
          value: '-0.017em',
          description: 'Letter spacing for medium body XL text'
        },
        {
          name: '--yz-font-medium-body-lg-size',
          value: '18px',
          description: 'Font size for medium body large text'
        },
        {
          name: '--yz-font-medium-body-lg-weight',
          value: '500',
          description: 'Font weight for medium body large text'
        },
        {
          name: '--yz-font-medium-body-lg-line-height',
          value: '28px',
          description: 'Line height for medium body large text'
        },
        {
          name: '--yz-font-medium-body-lg-letter-spacing',
          value: '-0.014em',
          description: 'Letter spacing for medium body large text'
        },
        {
          name: '--yz-font-medium-body-md-size',
          value: '16px',
          description: 'Font size for medium body medium text'
        },
        {
          name: '--yz-font-medium-body-md-weight',
          value: '500',
          description: 'Font weight for medium body medium text'
        },
        {
          name: '--yz-font-medium-body-md-line-height',
          value: '24px',
          description: 'Line height for medium body medium text'
        },
        {
          name: '--yz-font-medium-body-md-letter-spacing',
          value: '-0.011em',
          description: 'Letter spacing for medium body medium text'
        },
        {
          name: '--yz-font-medium-body-sm-size',
          value: '14px',
          description: 'Font size for medium body small text'
        },
        {
          name: '--yz-font-medium-body-sm-weight',
          value: '500',
          description: 'Font weight for medium body small text'
        },
        {
          name: '--yz-font-medium-body-sm-line-height',
          value: '20px',
          description: 'Line height for medium body small text'
        },
        {
          name: '--yz-font-medium-body-sm-letter-spacing',
          value: '-0.006em',
          description: 'Letter spacing for medium body small text'
        },
        {
          name: '--yz-font-medium-body-xs-size',
          value: '12px',
          description: 'Font size for medium body XS text'
        },
        {
          name: '--yz-font-medium-body-xs-weight',
          value: '500',
          description: 'Font weight for medium body XS text'
        },
        {
          name: '--yz-font-medium-body-xs-line-height',
          value: '16px',
          description: 'Line height for medium body XS text'
        },
        {
          name: '--yz-font-medium-body-xs-letter-spacing',
          value: '0em',
          description: 'Letter spacing for medium body XS text'
        },
        {
          name: '--yz-font-medium-body-2xs-size',
          value: '10px',
          description: 'Font size for medium body 2XS text'
        },
        {
          name: '--yz-font-medium-body-2xs-weight',
          value: '500',
          description: 'Font weight for medium body 2XS text'
        },
        {
          name: '--yz-font-medium-body-2xs-line-height',
          value: '16px',
          description: 'Line height for medium body 2XS text'
        },
        {
          name: '--yz-font-medium-body-2xs-letter-spacing',
          value: '0em',
          description: 'Letter spacing for medium body 2XS text'
        },

        // Bold Body Typography Variables
        {
          name: '--yz-font-bold-body-xl-size',
          value: '20px',
          description: 'Font size for bold body XL text'
        },
        {
          name: '--yz-font-bold-body-xl-weight',
          value: '700',
          description: 'Font weight for bold body XL text'
        },
        {
          name: '--yz-font-bold-body-xl-line-height',
          value: '32px',
          description: 'Line height for bold body XL text'
        },
        {
          name: '--yz-font-bold-body-xl-letter-spacing',
          value: '-0.017em',
          description: 'Letter spacing for bold body XL text'
        },
        {
          name: '--yz-font-bold-body-lg-size',
          value: '18px',
          description: 'Font size for bold body large text'
        },
        {
          name: '--yz-font-bold-body-lg-weight',
          value: '700',
          description: 'Font weight for bold body large text'
        },
        {
          name: '--yz-font-bold-body-lg-line-height',
          value: '28px',
          description: 'Line height for bold body large text'
        },
        {
          name: '--yz-font-bold-body-lg-letter-spacing',
          value: '-0.014em',
          description: 'Letter spacing for bold body large text'
        },
        {
          name: '--yz-font-bold-body-md-size',
          value: '16px',
          description: 'Font size for bold body medium text'
        },
        {
          name: '--yz-font-bold-body-md-weight',
          value: '700',
          description: 'Font weight for bold body medium text'
        },
        {
          name: '--yz-font-bold-body-md-line-height',
          value: '24px',
          description: 'Line height for bold body medium text'
        },
        {
          name: '--yz-font-bold-body-md-letter-spacing',
          value: '-0.011em',
          description: 'Letter spacing for bold body medium text'
        },
        {
          name: '--yz-font-bold-body-sm-size',
          value: '14px',
          description: 'Font size for bold body small text'
        },
        {
          name: '--yz-font-bold-body-sm-weight',
          value: '700',
          description: 'Font weight for bold body small text'
        },
        {
          name: '--yz-font-bold-body-sm-line-height',
          value: '20px',
          description: 'Line height for bold body small text'
        },
        {
          name: '--yz-font-bold-body-sm-letter-spacing',
          value: '-0.006em',
          description: 'Letter spacing for bold body small text'
        },
        {
          name: '--yz-font-bold-body-xs-size',
          value: '12px',
          description: 'Font size for bold body XS text'
        },
        {
          name: '--yz-font-bold-body-xs-weight',
          value: '700',
          description: 'Font weight for bold body XS text'
        },
        {
          name: '--yz-font-bold-body-xs-line-height',
          value: '16px',
          description: 'Line height for bold body XS text'
        },
        {
          name: '--yz-font-bold-body-xs-letter-spacing',
          value: '0em',
          description: 'Letter spacing for bold body XS text'
        },
        {
          name: '--yz-font-bold-body-2xs-size',
          value: '10px',
          description: 'Font size for bold body 2XS text'
        },
        {
          name: '--yz-font-bold-body-2xs-weight',
          value: '700',
          description: 'Font weight for bold body 2XS text'
        },
        {
          name: '--yz-font-bold-body-2xs-line-height',
          value: '16px',
          description: 'Line height for bold body 2XS text'
        },
        {
          name: '--yz-font-bold-body-2xs-letter-spacing',
          value: '0em',
          description: 'Letter spacing for bold body 2XS text'
        },

        // Link Body Typography Variables
        {
          name: '--yz-font-link-body-xl-size',
          value: '20px',
          description: 'Font size for link body XL text'
        },
        {
          name: '--yz-font-link-body-xl-weight',
          value: '400',
          description: 'Font weight for link body XL text'
        },
        {
          name: '--yz-font-link-body-xl-line-height',
          value: '32px',
          description: 'Line height for link body XL text'
        },
        {
          name: '--yz-font-link-body-xl-letter-spacing',
          value: '-0.017em',
          description: 'Letter spacing for link body XL text'
        },
        {
          name: '--yz-font-link-body-lg-size',
          value: '18px',
          description: 'Font size for link body large text'
        },
        {
          name: '--yz-font-link-body-lg-weight',
          value: '400',
          description: 'Font weight for link body large text'
        },
        {
          name: '--yz-font-link-body-lg-line-height',
          value: '28px',
          description: 'Line height for link body large text'
        },
        {
          name: '--yz-font-link-body-lg-letter-spacing',
          value: '-0.014em',
          description: 'Letter spacing for link body large text'
        },
        {
          name: '--yz-font-link-body-md-size',
          value: '16px',
          description: 'Font size for link body medium text'
        },
        {
          name: '--yz-font-link-body-md-weight',
          value: '400',
          description: 'Font weight for link body medium text'
        },
        {
          name: '--yz-font-link-body-md-line-height',
          value: '24px',
          description: 'Line height for link body medium text'
        },
        {
          name: '--yz-font-link-body-md-letter-spacing',
          value: '-0.011em',
          description: 'Letter spacing for link body medium text'
        },
        {
          name: '--yz-font-link-body-sm-size',
          value: '14px',
          description: 'Font size for link body small text'
        },
        {
          name: '--yz-font-link-body-sm-weight',
          value: '400',
          description: 'Font weight for link body small text'
        },
        {
          name: '--yz-font-link-body-sm-line-height',
          value: '20px',
          description: 'Line height for link body small text'
        },
        {
          name: '--yz-font-link-body-sm-letter-spacing',
          value: '-0.006em',
          description: 'Letter spacing for link body small text'
        },
        {
          name: '--yz-font-link-body-xs-size',
          value: '12px',
          description: 'Font size for link body XS text'
        },
        {
          name: '--yz-font-link-body-xs-weight',
          value: '400',
          description: 'Font weight for link body XS text'
        },
        {
          name: '--yz-font-link-body-xs-line-height',
          value: '16px',
          description: 'Line height for link body XS text'
        },
        {
          name: '--yz-font-link-body-xs-letter-spacing',
          value: '0em',
          description: 'Letter spacing for link body XS text'
        },
        {
          name: '--yz-font-link-body-2xs-size',
          value: '10px',
          description: 'Font size for link body 2XS text'
        },
        {
          name: '--yz-font-link-body-2xs-weight',
          value: '400',
          description: 'Font weight for link body 2XS text'
        },
        {
          name: '--yz-font-link-body-2xs-line-height',
          value: '16px',
          description: 'Line height for link body 2XS text'
        },
        {
          name: '--yz-font-link-body-2xs-letter-spacing',
          value: '0em',
          description: 'Letter spacing for link body 2XS text'
        },

        // Link Button Typography Variables
        {
          name: '--yz-font-link-button-xl-size',
          value: '20px',
          description: 'Font size for link button XL text'
        },
        {
          name: '--yz-font-link-button-xl-weight',
          value: '600',
          description: 'Font weight for link button XL text'
        },
        {
          name: '--yz-font-link-button-xl-line-height',
          value: '32px',
          description: 'Line height for link button XL text'
        },
        {
          name: '--yz-font-link-button-xl-letter-spacing',
          value: '-0.017em',
          description: 'Letter spacing for link button XL text'
        },
        {
          name: '--yz-font-link-button-lg-size',
          value: '18px',
          description: 'Font size for link button large text'
        },
        {
          name: '--yz-font-link-button-lg-weight',
          value: '600',
          description: 'Font weight for link button large text'
        },
        {
          name: '--yz-font-link-button-lg-line-height',
          value: '28px',
          description: 'Line height for link button large text'
        },
        {
          name: '--yz-font-link-button-lg-letter-spacing',
          value: '-0.014em',
          description: 'Letter spacing for link button large text'
        },
        {
          name: '--yz-font-link-button-md-size',
          value: '16px',
          description: 'Font size for link button medium text'
        },
        {
          name: '--yz-font-link-button-md-weight',
          value: '600',
          description: 'Font weight for link button medium text'
        },
        {
          name: '--yz-font-link-button-md-line-height',
          value: '24px',
          description: 'Line height for link button medium text'
        },
        {
          name: '--yz-font-link-button-md-letter-spacing',
          value: '-0.011em',
          description: 'Letter spacing for link button medium text'
        },
        {
          name: '--yz-font-link-button-sm-size',
          value: '14px',
          description: 'Font size for link button small text'
        },
        {
          name: '--yz-font-link-button-sm-weight',
          value: '600',
          description: 'Font weight for link button small text'
        },
        {
          name: '--yz-font-link-button-sm-line-height',
          value: '20px',
          description: 'Line height for link button small text'
        },
        {
          name: '--yz-font-link-button-sm-letter-spacing',
          value: '-0.006em',
          description: 'Letter spacing for link button small text'
        },
        {
          name: '--yz-font-link-button-xs-size',
          value: '12px',
          description: 'Font size for link button XS text'
        },
        {
          name: '--yz-font-link-button-xs-weight',
          value: '600',
          description: 'Font weight for link button XS text'
        },
        {
          name: '--yz-font-link-button-xs-line-height',
          value: '16px',
          description: 'Line height for link button XS text'
        },
        {
          name: '--yz-font-link-button-xs-letter-spacing',
          value: '0em',
          description: 'Letter spacing for link button XS text'
        },
        {
          name: '--yz-font-link-button-2xs-size',
          value: '10px',
          description: 'Font size for link button 2XS text'
        },
        {
          name: '--yz-font-link-button-2xs-weight',
          value: '600',
          description: 'Font weight for link button 2XS text'
        },
        {
          name: '--yz-font-link-button-2xs-line-height',
          value: '16px',
          description: 'Line height for link button 2XS text'
        },
        {
          name: '--yz-font-link-button-2xs-letter-spacing',
          value: '0em',
          description: 'Letter spacing for link button 2XS text'
        },

        // Rounded Typography Variables
        {
          name: '--yz-font-rounded-medium-7-size',
          value: '18px',
          description: 'Font size for rounded medium 7 typography'
        },
        {
          name: '--yz-font-rounded-medium-7-weight',
          value: '350',
          description: 'Font weight for rounded medium 7 typography'
        },
        {
          name: '--yz-font-rounded-medium-7-line-height',
          value: '28px',
          description: 'Line height for rounded medium 7 typography'
        },
        {
          name: '--yz-font-rounded-medium-7-letter-spacing',
          value: '-0.014em',
          description: 'Letter spacing for rounded medium 7 typography'
        },

        // Form and Dialog Typography Variables
        {
          name: '--yz-font-form-field-size',
          value: '14px',
          description: 'Font size for form fields'
        },
        {
          name: '--yz-font-dialog-title-size',
          value: '16px',
          description: 'Font size for dialog titles'
        },
        {
          name: '--yz-font-dialog-title-weight',
          value: '500',
          description: 'Font weight for dialog titles'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.css-properties {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 12px;
}

.css-props {
  display: block;
  margin-bottom: 4px;
  line-height: 1.4;
}

.property-name {
  color: #0066cc;
  font-weight: 500;
}

.property-value {
  color: #d73a49;
}
</style>